"use client";

import { useState, useEffect, useMemo } from "react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { infoCenterApi } from "@/api/query/info-center";
import { InfoCard } from "@/components/info-center/info-card";
import { CheckCircle, Search, X } from "lucide-react";
import { useRouter } from "next/navigation";

// Tab order priority — "news" always first, then known types, then anything else
const TAB_ORDER = ["news", "events", "vacancy", "others"];

function normalizeType(type: string): string {
  const lower = type.toLowerCase();
  if (TAB_ORDER.includes(lower)) return lower;
  return "others";
}

export default function InfoCenterPageClient({
  initialData,
}: {
  initialData: Record<string, any[]>;
}) {
  const router = useRouter();
  const [imageUrls, setImageUrls] = useState<Record<number, string>>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("news");

  const allItems = useMemo(() => {
    return Object.entries(initialData).flatMap(([type, items]) =>
      items.map((item) => ({
        ...item,
        type,
        normalizedType: normalizeType(type),
      })),
    );
  }, [initialData]);

  // Determine which tabs actually have content
  const availableTabs = useMemo(() => {
    const presentTypes = new Set(allItems.map((item) => item.normalizedType));
    return TAB_ORDER.filter((tab) => presentTypes.has(tab));
  }, [allItems]);

  // Keep activeTab valid if data changes
  useEffect(() => {
    if (availableTabs.length > 0 && !availableTabs.includes(activeTab)) {
      setActiveTab(availableTabs[0]);
    }
  }, [availableTabs]);

  const tabFilteredItems = useMemo(() => {
    return allItems.filter((item) => item.normalizedType === activeTab);
  }, [allItems, activeTab]);

  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return tabFilteredItems;
    const query = searchQuery.toLowerCase();
    return tabFilteredItems.filter((item) => {
      const title = item.infoMaterial.replace(/<[^>]*>/g, "").toLowerCase();
      const type = item.type.toLowerCase();
      return title.includes(query) || type.includes(query);
    });
  }, [tabFilteredItems, searchQuery]);

  // Fetch images client-side after mount (blobs can't be serialized from server)
  useEffect(() => {
    const fetchImages = async () => {
      const urls: Record<number, string> = {};
      await Promise.all(
        allItems.map(async (item) => {
          try {
            const blob = await infoCenterApi.getInfoCenterFilePics(item.id);
            urls[item.id] = URL.createObjectURL(blob);
          } catch {}
        }),
      );
      setImageUrls(urls);
    };

    if (allItems.length > 0) fetchImages();

    return () => {
      Object.values(imageUrls).forEach((url) => URL.revokeObjectURL(url));
    };
  }, [allItems]);

  const tabLabels: Record<string, string> = {
    news: "News",
    events: "Events",
    vacancy: "Vacancies",
    others: "Others",
  };

  const tabCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    allItems.forEach((item) => {
      counts[item.normalizedType] = (counts[item.normalizedType] || 0) + 1;
    });
    return counts;
  }, [allItems]);

  return (
    <main>
      <Header />

      {/* Hero */}
      <div className="bg-[#21205A] text-white py-12 md:py-16 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-2xl md:text-4xl lg:text-4xl font-bold mb-2">
            Information Center
          </h1>
          <p
            className="font-bold text-xl md:text-2xl leading-tight max-w-3xl mx-auto min-h-[3rem] md:min-h-[4rem]"
            style={{
              backgroundImage: "linear-gradient(90deg, #EFAE33, #B97BFF)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
              display: "inline-block",
            }}
          >
            Stay Informed with News, Events, and Opportunities
          </p>
        </div>
      </div>

      <div className="min-h-screen bg-white">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="mb-8 text-center md:text-left">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
              Stay updated with the latest at La Plage Meta Verse
            </h2>
            <p className="text-gray-600 text-lg">
              Browse all news, vacancies, events, and announcements
            </p>
          </div>

          {/* Search Bar */}
          {allItems.length > 0 && (
            <div className="mb-6">
              <div className="relative max-w-3xl">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search by title or category... e.g event, news"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#21205A] focus:border-transparent outline-none transition-all"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center hover:text-gray-700 text-gray-400"
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>
              {searchQuery && (
                <p className="mt-3 text-gray-600">
                  Found {filteredItems.length} result
                  {filteredItems.length !== 1 ? "s" : ""} in{" "}
                  <span className="font-medium">{tabLabels[activeTab]}</span>
                </p>
              )}
            </div>
          )}

          {/* Type Tabs */}
          {availableTabs.length > 1 && (
            <div className="mb-8">
              <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-0">
                {availableTabs.map((tab) => (
                  <button
                    key={tab}
                    onClick={() => {
                      setActiveTab(tab);
                      setSearchQuery("");
                    }}
                    className={`
                      relative px-5 py-2.5 text-sm font-semibold rounded-t-lg transition-all duration-200 flex items-center gap-2
                      ${
                        activeTab === tab
                          ? "bg-[#21205A] text-white shadow-sm -mb-px border border-b-0 border-[#21205A]"
                          : "text-gray-500 hover:text-[#21205A] hover:bg-gray-50 border border-transparent"
                      }
                    `}
                  >
                    {tabLabels[tab]}
                    <span
                      className={`
                        inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-bold
                        ${
                          activeTab === tab
                            ? "bg-white/20 text-white"
                            : "bg-gray-100 text-gray-500"
                        }
                      `}
                    >
                      {tabCounts[tab] || 0}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Cards Grid */}
          {allItems.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">
                No information available at the moment.
              </p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-12">
              <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              {searchQuery ? (
                <>
                  <p className="text-gray-500 text-lg mb-2">
                    No results found for "{searchQuery}"
                  </p>
                  <p className="text-gray-400">
                    Try adjusting your search terms
                  </p>
                </>
              ) : (
                <p className="text-gray-500 text-lg">
                  No {tabLabels[activeTab]?.toLowerCase()} available at the
                  moment.
                </p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredItems.map((item) => (
                <InfoCard
                  key={item.id}
                  slug={String(item.id)}
                  infoType={item.type}
                  title={item.infoMaterial
                    .replace(/<[^>]*>/g, "")
                    .substring(0, 60)}
                  description={item.infoMaterial}
                  imageUrl={imageUrls[item.id]}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-[#21205A] min-h-[60vh] flex items-center">
        <div className="max-w-6xl mx-auto text-center">
          <h2
            className="font-bold text-xl md:text-3xl mb-6"
            style={{
              backgroundImage: "linear-gradient(90deg, #EFAE33, #B97BFF)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
              display: "inline-block",
            }}
          >
            Ready to Transform Your Future?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto leading-relaxed">
            Join over 1.5 million graduates who have advanced their careers with
            La Plage Meta Verse. Start your learning journey today and unlock
            endless possibilities.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() =>
                router.push("https://student.laplagemetaverse.org/sign-in")
              }
              className="bg-white text-[#21205A] px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Browse Courses
            </button>
            <button
              onClick={() =>
                router.push("https://student.laplagemetaverse.org/sign-up")
              }
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-[#21205A] transition-colors"
            >
              Get Started Free
            </button>
          </div>
          <div className="mt-8 flex flex-wrap justify-center gap-8 text-blue-100 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              <span>Quality Certifications</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              <span>Instant Access</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              <span>Flexible Learning</span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
